import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Clock, Share2, Bookmark, ThumbsUp } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { blogPosts } from './BlogList';

interface BlogPostProps {
  postId?: string;
  onBack?: () => void;
}

export const BlogPost: React.FC<BlogPostProps> = ({ postId, onBack }) => {
  const prefersReducedMotion = useReducedMotion();

  // Get the blog post data based on postId
  const blogPost = blogPosts.find(post => post.id === postId) || blogPosts[0];

  // Mock content for the blog post
  const mockContent = `
    <p>In the rapidly evolving landscape of artificial intelligence, one of the most promising applications lies in understanding and improving human relationships. At Octify, we've harnessed the power of Google's Gemini 2.5 Flash to create a revolutionary approach to relationship analysis that goes far beyond traditional counseling methods.</p>

    <h2>Understanding the Technology</h2>
    <p>Our AI system analyzes patterns in communication, emotional responses, and behavioral data to provide insights that would take human counselors months to uncover. By processing thousands of data points from your interactions, mood logs, and even physiological responses from wearable devices, Gemini 2.5 Flash can identify subtle patterns that indicate relationship health and areas for improvement.</p>

    <h2>The Science of Pattern Recognition</h2>
    <p>Research in computational psychology has shown that relationship success can be predicted with remarkable accuracy by analyzing communication patterns. Our AI looks for:</p>
    <ul>
      <li>Frequency and timing of positive vs. negative interactions</li>
      <li>Emotional regulation patterns during conflicts</li>
      <li>Stress response correlations with relationship events</li>
      <li>Communication style compatibility metrics</li>
    </ul>

    <h2>Privacy-First Approach</h2>
    <p>While our AI is incredibly sophisticated, we've built it with privacy as the foundation. We never store raw conversation data or personal details. Instead, our system analyzes anonymized patterns and correlations, ensuring your most intimate moments remain private while still providing valuable insights.</p>

    <h2>Real-World Applications</h2>
    <p>Users have reported significant improvements in their relationships after implementing AI-suggested strategies. From better conflict resolution techniques to improved emotional awareness, the practical applications are transforming how people connect with their loved ones.</p>

    <h2>The Future of Relationship Technology</h2>
    <p>As AI continues to evolve, we're exploring new frontiers in relationship enhancement, including predictive modeling for relationship challenges and personalized intervention strategies. The goal isn't to replace human connection but to enhance our natural ability to understand and care for one another.</p>
  `;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blogPost.title,
        text: blogPost.title,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      console.log('Link copied to clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20 pt-20">
      {/* Structured Data for Article */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": blogPost.title,
          "description": blogPost.excerpt,
          "image": blogPost.image,
          "author": {
            "@type": "Person",
            "name": blogPost.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Octify",
            "logo": {
              "@type": "ImageObject",
              "url": "https://octify.app/logo.png"
            }
          },
          "datePublished": blogPost.date,
          "dateModified": blogPost.date,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": window.location.href
          },
          "keywords": blogPost.keywords || "relationship insights, AI advice, relationship tips"
        })}
      </script>

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #FF6B35 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #8B5CF6 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <motion.button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors duration-200 mb-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={prefersReducedMotion ? {} : { x: -5 }}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Blog
        </motion.button>

        {/* Article Header */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Category Badge */}
          <motion.div
            className="mb-6"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
              {blogPost.category.charAt(0).toUpperCase() + blogPost.category.slice(1).replace('-', ' ')}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {blogPost.title}
          </motion.h1>

          {/* Meta Information */}
          <motion.div
            className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-400 mb-8"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span className="font-medium">{blogPost.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(blogPost.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{blogPost.readTime}</span>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex items-center gap-4 mb-8"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <motion.button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-full text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Share2 className="w-4 h-4" />
              Share
            </motion.button>
            <motion.button
              className="flex items-center gap-2 px-4 py-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-full text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Bookmark className="w-4 h-4" />
              Save
            </motion.button>
            <motion.button
              className="flex items-center gap-2 px-4 py-2 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-full text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <ThumbsUp className="w-4 h-4" />
              Like
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Featured Image */}
        <motion.div
          className="mb-12 rounded-3xl overflow-hidden"
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img
            src={blogPost.image}
            alt={`${blogPost.title} - Featured image showing AI-powered relationship insights and technology`}
            className="w-full h-64 md:h-96 object-cover"
          />
        </motion.div>

        {/* Article Content */}
        <motion.div
          className="prose prose-lg max-w-none"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div 
            className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-6"
            dangerouslySetInnerHTML={{ __html: mockContent }}
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.75'
            }}
          />
        </motion.div>

        {/* Tags */}
        <motion.div
          className="mt-12 pt-8 border-t border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tags</h3>
          <div className="flex flex-wrap gap-3">
            {blogPost.keywords?.split(', ').slice(0, 6).map((tag, index) => (
              <motion.span
                key={tag}
                className="px-3 py-1 bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-full text-sm text-gray-700 dark:text-gray-300"
                initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
              >
                #{tag.trim()}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Author Bio */}
        <motion.div
          className="mt-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {blogPost.author}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {blogPost.author.includes('Dr.') 
                  ? `${blogPost.author} is a leading researcher in computational psychology and AI-human interaction. They hold a PhD from Stanford University and have published over 50 papers on relationship dynamics and artificial intelligence applications in mental health.`
                  : `${blogPost.author} is a relationship expert and writer specializing in AI-powered relationship insights and modern communication strategies. They have helped thousands of people improve their relationships through evidence-based approaches.`
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Related Articles */}
        <motion.div
          className="mt-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Related Articles
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {blogPosts.filter(post => post.id !== blogPost.id).slice(0, 2).map((article, index) => (
              <motion.div
                key={article.id}
                className="p-6 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200 cursor-pointer"
                initial={prefersReducedMotion ? {} : { opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 hover:text-orange-500 transition-colors duration-200">
                  {article.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  {article.excerpt}
                </p>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {article.readTime}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};